import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import CartDrawer from "../../common/cartpage/CartDrawer";
import { ToastContainer, toast } from "react-toastify";
import { useMenu } from "../../context/MenuContext";
import "react-toastify/dist/ReactToastify.css";
import ProductSkeleton from "../../skeletons/ProductSkeleton";
import { FaStar } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Check,
  Minus,
  Plus,
  Truck,
  RotateCcw,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";

/* Brand accent — matches the red already used across the site. */
const ACCENT = "rgb(255,0,25)";

/* Edit these to match your real policy, or delete the block in the JSX. */
const TRUST_POINTS = [
  { icon: Truck, label: "Ships in 24–48 hrs" },
  { icon: RotateCcw, label: "7-day easy returns" },
  { icon: ShieldCheck, label: "Secure checkout" },
];

/* ---------- small presentational helpers ---------- */

const StarRow = ({ value = 0, size = "text-sm" }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <FaStar
        key={i}
        className={`${size} ${
          i < Math.round(value) ? "text-amber-400" : "text-gray-300"
        }`}
      />
    ))}
  </span>
);

const Section = ({ title, children, className = "" }) => (
  <div className={className}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
      {title}
    </p>
    <div className="mt-3">{children}</div>
  </div>
);

const ProductPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [tab, setTab] = useState("description");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const { fetchMenuCounts, setCartCount } = useMenu();
  const [addingToCart, setAddingToCart] = useState(false);
  const [zoomStyle, setZoomStyle] = useState({});
  const [isZooming, setIsZooming] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [errors, setErrors] = useState({});
  const [avgRating, setAvgRating] = useState(0);
  const [comment, setComment] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { slug } = useParams();
  const user = JSON.parse(localStorage.getItem("authUserId"));
  const id = user;
  const token =
    localStorage.getItem("authToken") || localStorage.getItem("guest_token");

  /* ---------- data ---------- */

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://code.gurusport.in/api/site-products/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json();
        const prod = data.data;

        setProduct({
          ...prod,
          reviews: prod.Review || [],
        });

        if (prod?.variants?.length > 0) {
          const defaultVariant =
            prod.variants.find((v) => v.status === 1) || prod.variants[0];

          setSelectedColor(defaultVariant.color_id);
          setSelectedSize(defaultVariant.size_id || "");

          const defaultImage =
            defaultVariant.variant_images?.find((img) => img.is_default)
              ?.path ||
            defaultVariant.variant_images?.[0]?.path ||
            prod.default_image ||
            "";

          setMainImage(defaultImage);
        } else {
          setMainImage(prod.default_image || "");
        }

        if (prod.Review && prod.Review.length > 0) {
          const total = prod.Review.reduce(
            (sum, r) => sum + Number(r.rating_value || 0),
            0
          );
          setAvgRating(total / prod.Review.length);
        } else {
          setAvgRating(0);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  /* ---------- derived values (hooks must stay above the early returns) ---------- */

  const currentVariant = useMemo(
    () =>
      product?.variants?.find(
        (v) => v.color_id === selectedColor && v.size_id === selectedSize
      ),
    [product, selectedColor, selectedSize]
  );

  const variantImages = useMemo(
    () => currentVariant?.variant_images || [],
    [currentVariant]
  );

  /* De-duplicated gallery — mainImage is usually already inside variantImages. */
  const galleryImages = useMemo(() => {
    const list = [];
    const push = (p) => {
      if (p && !list.includes(p)) list.push(p);
    };
    push(mainImage);
    variantImages.forEach((img) => push(img.path));
    push(product?.default_image);
    return list;
  }, [mainImage, variantImages, product]);

  const reviewStats = useMemo(() => {
    const list = product?.reviews || [];
    const buckets = [0, 0, 0, 0, 0];
    list.forEach((r) => {
      const v = Math.round(Number(r.rating_value || 0));
      if (v >= 1 && v <= 5) buckets[v - 1] += 1;
    });
    return { total: list.length, buckets };
  }, [product]);

  /* Lightbox keyboard controls */
  useEffect(() => {
    if (!isPopupOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsPopupOpen(false);
      if (e.key === "ArrowLeft")
        setCurrentIndex((p) => (p === 0 ? galleryImages.length - 1 : p - 1));
      if (e.key === "ArrowRight")
        setCurrentIndex((p) => (p === galleryImages.length - 1 ? 0 : p + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPopupOpen, galleryImages.length]);

  /* Stop the page scrolling behind overlays */
  useEffect(() => {
    const locked = isPopupOpen || showModal;
    document.body.style.overflow = locked ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isPopupOpen, showModal]);

  if (loading) return <ProductSkeleton />;
  if (!product)
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center">
        <p className="text-lg font-medium text-gray-900">
          We couldn't find that product.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 rounded-full border border-gray-300 px-6 py-2 text-sm font-semibold hover:border-gray-900"
        >
          Go back
        </button>
      </div>
    );

  /* ---------- pricing ---------- */

  const basePrice = parseFloat(product?.cost_price) || 0;
  const sellingPrice = parseFloat(product?.selling_price) || 0;
  const hasDiscount = sellingPrice > 0 && sellingPrice < basePrice;
  const unitPrice = hasDiscount ? sellingPrice : basePrice;
  const totalPrice = (quantity * unitPrice).toFixed(2);
  const discountPct = hasDiscount
    ? Math.round(((basePrice - sellingPrice) / basePrice) * 100)
    : 0;

  const maxStock = currentVariant?.stock || 0;
  const inStock = maxStock > 0;
  const lowStock = inStock && maxStock <= 5;
  const displayRating = avgRating || Number(product?.star) || 0;

  const colorOptions = [
    ...new Map((product?.variants || []).map((v) => [v.color_id, v])).values(),
  ];
  const sizeOptions = [
    ...new Map(
      (product?.variants || [])
        .filter((v) => v.color_id === selectedColor)
        .map((v) => [v.size_id, v])
    ).values(),
  ];
  const selectedColorName = product?.variants?.find(
    (v) => v.color_id === selectedColor
  )?.color_name;
  const selectedSizeName = product?.variants?.find(
    (v) => v.size_id === selectedSize
  )?.size_name;

  const metaLinks = (product?.head_categories || [])
    .concat(product?.parent_categories || [])
    .concat(product?.child_categories || []);

  /* ---------- actions ---------- */

  const handleAddToCart = async () => {
    if (!product?.id) return;

    if (!selectedColor || !selectedSize) {
      toast.warning("Please select color and size.");
      return;
    }

    const payload = {
      product_id: product.id,
      color_id: selectedColor,
      size_id: selectedSize,
      quantity,
    };

    setAddingToCart(true);

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let url = `https://code.gurusport.in/api/cart`;
      let headers = { "Content-Type": "application/json" };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setCartCount((prev) => prev + 1);
        } else {
          fetchMenuCounts(id, token);
        }

        setCartOpen(true);

        toast.success(data.message || "Product added to cart!", {
          position: "top-right",
          autoClose: 700,
          hideProgressBar: true,
          pauseOnHover: false,
          draggable: false,
          closeOnClick: true,
        });
      } else {
        toast.error(data.message || "Failed to add to cart.");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleSave = async () => {
    let newErrors = {};

    if (!guestName.trim()) {
      newErrors.guestName = "Enter your name";
    }

    if (!guestEmail.trim()) {
      newErrors.guestEmail = "Enter your email";
    }

    if (rating === 0) {
      newErrors.rating = "Pick a star rating";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    if (!product?.id) {
      console.error("No product selected!");
      Swal.fire("Error", "Please select a product first", "error");
      return;
    }

    try {
      const response = await axios.post(
        "https://code.gurusport.in/api/product-reviews",
        {
          product_id: product.id,
          rating_value: rating,
          rating_title: "Rating",
          rating_desc: comment,
          guest_name: guestName,
          guest_email: guestEmail,
          is_active: 1,
        }
      );

      console.log("Review Response:", response.data);
      Swal.fire("Success", "Review submitted successfully!", "success");

      setShowModal(false);
      setRating(0);
      setComment("");
      setGuestName("");
      setGuestEmail("");
    } catch (error) {
      console.error("Error submitting review:", error.response || error);
      Swal.fire("Error", "Failed to submit review", "error");
    }
  };

  /* Zoom — uses viewport coords so it stays accurate after scrolling. */
  const handleMouseEnter = () => setIsZooming(true);

  const handleMouseMove = (e) => {
    if (!isZooming) return;
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({ backgroundPosition: `${x}% ${y}%` });
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
    setZoomStyle({ backgroundPosition: "center" });
  };

  const openPopup = () => {
    const idx = galleryImages.indexOf(mainImage);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setIsPopupOpen(true);
  };

  const closePopup = () => setIsPopupOpen(false);

  const prevImage = () =>
    setCurrentIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );

  const nextImage = () =>
    setCurrentIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );

  const pickColor = (color) => {
    setSelectedColor(color.color_id);
    const sizesForColor = (product.variants || []).filter(
      (v) => v.color_id === color.color_id
    );
    if (sizesForColor.length > 0) {
      setSelectedSize(sizesForColor[0].size_id);
      const defImg =
        sizesForColor[0].variant_images?.find((i) => i.is_default)?.path ||
        sizesForColor[0].variant_images?.[0]?.path ||
        product.default_image;
      setMainImage(defImg);
    }
    setQuantity(1);
  };

  const pickSize = (size) => {
    setSelectedSize(size.size_id);
    const defImg =
      size.variant_images?.find((i) => i.is_default)?.path ||
      size.variant_images?.[0]?.path ||
      product.default_image;
    setMainImage(defImg);
    setQuantity(1);
  };

  /* ---------- render ---------- */

  return (
    <div className="bg-white text-gray-900">
      <ToastContainer />

      <div className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-20">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="mb-6 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-400"
        >
          <Link to="/" className="transition hover:text-gray-900">
            Home
          </Link>
          <span aria-hidden="true">/</span>
          <span>{product?.head_categories?.[0]?.name || "Category"}</span>
          <span aria-hidden="true">/</span>
          <span className="text-gray-900">{product?.name}</span>
        </nav>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start lg:gap-12">
          {/* ---------------- Gallery ---------------- */}
          <section className="lg:sticky lg:top-6 lg:col-span-7 lg:self-start">
            <div className="flex gap-4">
              {/* Vertical thumbnail rail (desktop) */}
              {galleryImages.length > 1 && (
                <div className="hidden w-20 shrink-0 flex-col gap-3 lg:flex">
                  {galleryImages.map((path, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setMainImage(path)}
                      aria-label={`View image ${idx + 1}`}
                      aria-current={mainImage === path}
                      className={`aspect-square overflow-hidden rounded-xl border bg-gray-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${
                        mainImage === path
                          ? "border-gray-900"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <img
                        src={path}
                        alt=""
                        className="h-full w-full object-contain p-1.5"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Main image */}
              <div className="relative flex-1">
                <div
                  className="group relative aspect-square max-h-[68vh] w-full cursor-zoom-in overflow-hidden rounded-3xl border border-gray-200 bg-gray-50"
                  style={{
                    backgroundImage: `url(${mainImage})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: isZooming ? "220%" : "contain",
                    backgroundPosition:
                      zoomStyle.backgroundPosition || "center",
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onClick={openPopup}
                >
                  {/* keeps the box in the layout flow for older browsers */}
                  <img
                    src={mainImage}
                    alt={product?.name}
                    className="h-full w-full object-contain opacity-0"
                  />

                  {hasDiscount && (
                    <span
                      className="absolute left-4 top-4 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white"
                      style={{ background: ACCENT }}
                    >
                      {discountPct}% off
                    </span>
                  )}

                  <span className="pointer-events-none absolute bottom-4 left-1/2 hidden -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-gray-600 shadow-sm backdrop-blur transition group-hover:block lg:block">
                    Hover to zoom · click to expand
                  </span>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPopup();
                    }}
                    aria-label="Open full screen gallery"
                    className="absolute right-4 top-4 rounded-full bg-white/90 p-2.5 text-gray-900 shadow-sm backdrop-blur transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Horizontal thumbnails (mobile / tablet) */}
            {galleryImages.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1 lg:hidden">
                {galleryImages.map((path, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setMainImage(path)}
                    aria-label={`View image ${idx + 1}`}
                    className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border bg-gray-50 ${
                      mainImage === path
                        ? "border-gray-900"
                        : "border-gray-200"
                    }`}
                  >
                    <img
                      src={path}
                      alt=""
                      className="h-full w-full object-contain p-1.5"
                    />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* ---------------- Buy box ---------------- */}
          <aside className="lg:col-span-5">
            <div>
              <h1 className="text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
                {product?.name}
              </h1>

              <button
                type="button"
                onClick={() => {
                  setTab("reviews");
                  document
                    .getElementById("product-tabs")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-2 inline-flex items-center gap-2 text-sm text-gray-500 transition hover:text-gray-900"
              >
                <StarRow value={displayRating} />
                <span>
                  {displayRating ? displayRating.toFixed(1) : "New"} ·{" "}
                  {product?.total_ratings || reviewStats.total || 0} reviews
                </span>
              </button>

              {/* Price */}
              <div className="mt-5 flex flex-wrap items-baseline gap-3">
                <span className="text-3xl font-bold tracking-tight">
                  ₹{unitPrice.toFixed(2)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-lg text-gray-400 line-through">
                      ₹{basePrice.toFixed(2)}
                    </span>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: ACCENT }}
                    >
                      Save ₹{(basePrice - sellingPrice).toFixed(2)}
                    </span>
                  </>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Inclusive of all taxes
              </p>

              {product?.short_description && (
                <div
                  className="mt-5 text-sm leading-relaxed text-gray-600 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: product.short_description,
                  }}
                />
              )}

              <div className="my-6 h-px bg-gray-200" />

              {/* Colour */}
              {colorOptions.length > 0 && (
                <Section
                  title={
                    selectedColorName ? `Colour · ${selectedColorName}` : "Colour"
                  }
                >
                  <div className="flex flex-wrap gap-3">
                    {colorOptions.map((color) => {
                      const active = selectedColor === color.color_id;
                      return (
                        <button
                          key={color.color_id}
                          type="button"
                          title={color.color_name}
                          aria-label={color.color_name}
                          aria-pressed={active}
                          onClick={() => pickColor(color)}
                          className={`relative h-9 w-9 rounded-full border border-black/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${
                            active
                              ? "ring-2 ring-gray-900 ring-offset-2"
                              : "hover:scale-105"
                          }`}
                          style={{
                            background: color.color_code_2
                              ? `linear-gradient(135deg, ${color.color_code} 50%, ${color.color_code_2} 50%)`
                              : color.color_code || "#ccc",
                          }}
                        >
                          {active && (
                            <Check
                              size={16}
                              className="absolute inset-0 m-auto text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Size */}
              {sizeOptions.length > 0 && (
                <Section
                  title={selectedSizeName ? `Size · ${selectedSizeName}` : "Size"}
                  className="mt-6"
                >
                  <div className="flex flex-wrap gap-2">
                    {sizeOptions.map((size, idx) => {
                      const active = selectedSize === size.size_id;
                      const soldOut = !(size.stock > 0);
                      return (
                        <button
                          key={idx}
                          type="button"
                          aria-pressed={active}
                          onClick={() => pickSize(size)}
                          className={`h-11 min-w-[3rem] rounded-xl border px-4 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${
                            active
                              ? "border-gray-900 bg-gray-900 text-white"
                              : "border-gray-300 hover:border-gray-900"
                          } ${soldOut && !active ? "text-gray-400 line-through" : ""}`}
                        >
                          {size.size_name}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Quantity + stock */}
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <div className="inline-flex items-center rounded-full border border-gray-300">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    className="grid h-11 w-11 place-items-center rounded-l-full text-gray-700 transition hover:bg-gray-100 disabled:opacity-40"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-10 text-center text-sm font-semibold tabular-nums">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    className="grid h-11 w-11 place-items-center rounded-r-full text-gray-700 transition hover:bg-gray-100 disabled:opacity-40"
                    onClick={() => setQuantity((q) => q + 1)}
                    disabled={quantity >= maxStock}
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <span
                  className={`inline-flex items-center gap-2 text-sm font-medium ${
                    inStock ? "text-emerald-600" : "text-gray-400"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      inStock ? "bg-emerald-500" : "bg-gray-300"
                    }`}
                  />
                  {inStock
                    ? lowStock
                      ? `Only ${maxStock} left`
                      : "In stock"
                    : "Out of stock"}
                </span>
              </div>

              {/* Add to cart */}
              <button
                onClick={handleAddToCart}
                disabled={!currentVariant || !inStock || addingToCart}
                className="mt-6 hidden h-14 w-2/3 items-center justify-center gap-2 rounded-full text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:brightness-90 disabled:cursor-not-allowed disabled:bg-gray-300 lg:inline-flex"
                style={{
                  background:
                    !currentVariant || !inStock ? undefined : ACCENT,
                }}
              >
                <ShoppingBag size={18} />
                {addingToCart
                  ? "Adding…"
                  : !inStock
                  ? "Out of stock"
                  : `Add to cart · ₹${totalPrice}`}
              </button>

              {/* Trust row */}
              <ul className="mt-6 grid grid-cols-1 gap-3 rounded-2xl bg-gray-50 p-4 sm:grid-cols-3">
                {TRUST_POINTS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-center gap-2 text-xs text-gray-600 sm:flex-col sm:text-center"
                  >
                    <Icon size={18} className="text-gray-900" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>

              {/* Meta */}
              <dl className="mt-6 space-y-3 border-t border-gray-200 pt-6 text-sm">
                <div className="flex gap-3">
                  <dt className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                    SKU
                  </dt>
                  <dd className="font-medium tracking-wide">
                    {product?.sku?.toUpperCase() || "N/A"}
                  </dd>
                </div>

                {metaLinks.length > 0 && (
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                      Category
                    </dt>
                    <dd className="flex flex-wrap gap-x-1.5 gap-y-1 text-gray-700">
                      {metaLinks.map((c, i) => (
                        <span key={i}>
                          <Link className="hover:text-gray-900 hover:underline">
                            {c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                          </Link>
                          {i < metaLinks.length - 1 ? "," : ""}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}

                {(product?.tags || []).length > 0 && (
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                      Tags
                    </dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {product.tags.map((t, i) => (
                        <Link
                          key={i}
                          className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600 transition hover:bg-gray-200"
                        >
                          {t.name.charAt(0).toUpperCase() + t.name.slice(1)}
                        </Link>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </aside>
        </div>

        {/* ---------------- Tabs ---------------- */}
        <div
          id="product-tabs"
          className="mt-12 scroll-mt-24 border-t border-gray-200 pt-10"
        >
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            {[
              { key: "description", label: "Description" },
              {
                key: "reviews",
                label: `Reviews (${reviewStats.total})`,
              },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 ${
                  tab === t.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "description" && (
              <div
                className="max-w-3xl text-[15px] leading-relaxed text-gray-700 [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-900 [&_li]:mb-1 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: product?.description || "" }}
              />
            )}

            {tab === "reviews" && (
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                {/* Summary */}
                <div className="lg:col-span-4">
                  <div className="rounded-2xl border border-gray-200 p-6">
                    <p className="text-4xl font-bold tracking-tight">
                      {displayRating ? displayRating.toFixed(1) : "—"}
                    </p>
                    <StarRow value={displayRating} size="text-base" />
                    <p className="mt-1 text-sm text-gray-500">
                      Based on {reviewStats.total}{" "}
                      {reviewStats.total === 1 ? "review" : "reviews"}
                    </p>

                    <div className="mt-5 space-y-2">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = reviewStats.buckets[star - 1];
                        const pct = reviewStats.total
                          ? (count / reviewStats.total) * 100
                          : 0;
                        return (
                          <div
                            key={star}
                            className="flex items-center gap-3 text-xs text-gray-500"
                          >
                            <span className="w-3 tabular-nums">{star}</span>
                            <FaStar className="text-amber-400" />
                            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                              <span
                                className="block h-full rounded-full bg-amber-400"
                                style={{ width: `${pct}%` }}
                              />
                            </span>
                            <span className="w-6 text-right tabular-nums">
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setShowModal(true)}
                      className="mt-6 h-12 w-full rounded-full border border-gray-900 text-sm font-semibold transition hover:bg-gray-900 hover:text-white"
                    >
                      Write a review
                    </button>
                  </div>
                </div>

                {/* List */}
                <div className="lg:col-span-8">
                  {product?.reviews?.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {product.reviews.map((r, idx) => (
                        <li key={idx} className="flex gap-4 py-5 first:pt-0">
                          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                            {(r.guest_name || "A").charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold">
                                {r.guest_name || "Anonymous"}
                              </span>
                              <StarRow value={Number(r.rating_value || 0)} />
                            </div>
                            <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600">
                              {r.rating_desc}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center">
                      <p className="font-medium">No reviews yet</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Bought this? Tell other shoppers how it fits.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- Sticky mobile bar ---------------- */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-4">
          <div className="min-w-0">
            <p className="text-lg font-bold leading-none">₹{totalPrice}</p>
            <p className="mt-1 truncate text-xs text-gray-500">
              {selectedSizeName ? `Size ${selectedSizeName}` : "Select a size"}
              {selectedColorName ? ` · ${selectedColorName}` : ""}
            </p>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={!currentVariant || !inStock || addingToCart}
            className="ml-auto inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full text-sm font-bold uppercase tracking-wider text-white transition disabled:cursor-not-allowed disabled:bg-gray-300"
            style={{
              background: !currentVariant || !inStock ? undefined : ACCENT,
            }}
          >
            <ShoppingBag size={16} />
            {addingToCart ? "Adding…" : inStock ? "Add to cart" : "Sold out"}
          </button>
        </div>
      </div>

      {/* ---------------- Lightbox ---------------- */}
      {isPopupOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/90 p-4 backdrop-blur-sm"
          onClick={closePopup}
        >
          <button
            onClick={closePopup}
            aria-label="Close gallery"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"
          >
            <X size={22} />
          </button>

          <div
            className="relative flex w-full max-w-4xl items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentIndex]}
              alt={`${product?.name} ${currentIndex + 1}`}
              className="max-h-[80vh] max-w-full rounded-2xl object-contain"
            />

            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  aria-label="Previous image"
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={nextImage}
                  aria-label="Next image"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"
                >
                  <ChevronRight size={22} />
                </button>
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                  {currentIndex + 1} / {galleryImages.length}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Review modal ---------------- */}
      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end justify-center bg-gray-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight">
                  Write a review
                </h2>
                <p className="mt-1 text-sm text-gray-500">{product?.name}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                aria-label="Close"
                className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900"
              >
                <X size={18} />
              </button>
            </div>

            {/* Rating */}
            <div className="mt-6">
              <div className="flex justify-center gap-1.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    aria-label={`${star} star${star > 1 ? "s" : ""}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition hover:scale-110"
                  >
                    <FaStar
                      className={`text-3xl ${
                        star <= (hoverRating || rating)
                          ? "text-amber-400"
                          : "text-gray-200"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {errors.rating && (
                <p className="mt-2 text-center text-sm text-red-500">
                  {errors.rating}
                </p>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                  Name
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Your name"
                  className={`mt-1.5 h-12 w-full rounded-xl border px-4 text-sm outline-none transition focus:border-gray-900 ${
                    errors.guestName ? "border-red-400" : "border-gray-300"
                  }`}
                />
                {errors.guestName && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.guestName}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                  Email
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`mt-1.5 h-12 w-full rounded-xl border px-4 text-sm outline-none transition focus:border-gray-900 ${
                    errors.guestEmail ? "border-red-400" : "border-gray-300"
                  }`}
                />
                {errors.guestEmail && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.guestEmail}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                  Review
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="How is the fit, fabric and finish?"
                  className="mt-1.5 w-full resize-none rounded-xl border border-gray-300 p-4 text-sm outline-none transition focus:border-gray-900"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                onClick={handleSave}
                className="h-12 w-full rounded-full text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-90"
                style={{ background: ACCENT }}
              >
                Post review
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="h-12 w-full rounded-full border border-gray-300 text-sm font-semibold transition hover:border-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
};

export default ProductPage;